import Dexie, { type EntityTable } from 'dexie';
import type { ProjectRecord, ProjectSummary, RevisionRecord, StoryboardProject } from '../types';

class StoryboardSmithDB extends Dexie {
  projects!: EntityTable<ProjectRecord, 'id'>;
  revisions!: EntityTable<RevisionRecord, 'id'>;

  constructor() {
    super('StoryboardSmithDB');
    this.version(1).stores({
      projects: 'id, title, updatedAt, createdAt',
      revisions: 'id, projectId, createdAt, [projectId+createdAt]',
    });
  }
}

export const db = new StoryboardSmithDB();

const summaryFromProject = (project: StoryboardProject, previewDataUrl?: string): ProjectRecord => ({
  id: project.id,
  title: project.title,
  createdAt: project.createdAt,
  updatedAt: project.updatedAt,
  cellCount: project.cells.length,
  previewDataUrl,
  document: structuredClone(project),
});

export const saveProject = async (project: StoryboardProject, previewDataUrl?: string): Promise<void> => {
  const existing = await db.projects.get(project.id);
  await db.projects.put(summaryFromProject(project, previewDataUrl ?? existing?.previewDataUrl));
};

export const loadProject = async (id: string): Promise<StoryboardProject | undefined> => {
  const record = await db.projects.get(id);
  return record ? structuredClone(record.document) : undefined;
};

export const listProjects = async (): Promise<ProjectSummary[]> => {
  const records = await db.projects.orderBy('updatedAt').reverse().toArray();
  return records.map(({ document: _document, ...summary }) => summary);
};

export const listProjectRecords = async (): Promise<ProjectRecord[]> =>
  db.projects.orderBy('updatedAt').reverse().toArray();

export const deleteProject = async (id: string): Promise<void> => {
  await db.transaction('rw', db.projects, db.revisions, async () => {
    await db.projects.delete(id);
    await db.revisions.where('projectId').equals(id).delete();
  });
};

export const duplicateProjectRecord = async (project: StoryboardProject): Promise<void> => {
  await saveProject(project);
};

export const saveRevision = async (
  project: StoryboardProject,
  label = `Revision ${new Date().toLocaleString()}`,
): Promise<RevisionRecord> => {
  const revision: RevisionRecord = {
    id: `${project.id}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    projectId: project.id,
    createdAt: new Date().toISOString(),
    label,
    document: structuredClone(project),
  };
  await db.revisions.put(revision);

  const all = await db.revisions.where('projectId').equals(project.id).sortBy('createdAt');
  if (all.length > 30) {
    await db.revisions.bulkDelete(all.slice(0, all.length - 30).map((item) => item.id));
  }
  return revision;
};

export const listRevisions = async (projectId: string): Promise<RevisionRecord[]> => {
  const revisions = await db.revisions.where('projectId').equals(projectId).sortBy('createdAt');
  return revisions.reverse();
};

export const deleteRevision = async (id: string): Promise<void> => {
  await db.revisions.delete(id);
};

export const requestPersistentStorage = async (): Promise<'granted' | 'denied' | 'unsupported'> => {
  if (!navigator.storage?.persist) return 'unsupported';
  try {
    const granted = await navigator.storage.persist();
    return granted ? 'granted' : 'denied';
  } catch {
    return 'denied';
  }
};

export const storageEstimate = async (): Promise<{ usage: number; quota: number } | null> => {
  if (!navigator.storage?.estimate) return null;
  const estimate = await navigator.storage.estimate();
  return { usage: estimate.usage ?? 0, quota: estimate.quota ?? 0 };
};
